import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  User,
  Package,
  CreditCard,
  History,
  Calendar,
  Phone,
  Mail,
  School,
  ExternalLink,
  ArrowUpFromLine,
  ArrowDownToLine,
  ArrowRight,
  Repeat,
  RefreshCw,
  Pause,
  Play,
  XCircle,
  DollarSign,
  Clock,
  Plus,
  Trash2,
  ChevronDown,
  Check,
  ArrowRightLeft,
  Zap,
  Shield,
  Receipt,
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { API } from "../config/api";

const formatEST = (dateString, formatStr = 'MMM d, yyyy h:mm a') => {
  if (!dateString) return '-';
  try {
    let normalizedDate = dateString;
    if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
      normalizedDate = dateString + 'Z';
    }
    const date = typeof normalizedDate === 'string' ? parseISO(normalizedDate) : normalizedDate;
    return formatInTimeZone(date, 'America/New_York', formatStr);
  } catch {
    return '-';
  }
};

const formatUnixTimestamp = (timestamp) => {
  if (!timestamp) return '-';
  try {
    const date = new Date(timestamp * 1000);
    return formatInTimeZone(date, 'America/New_York', 'MMM d, yyyy h:mm a');
  } catch {
    return '-';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'Active': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
    case 'Completed': return 'bg-slate-100 text-slate-700 border-slate-200';
    case 'Cancelled': return 'bg-red-100 text-red-700 border-red-200';
    case 'Awaiting Pickup': return 'bg-amber-100 text-amber-700 border-amber-200';
    case 'Pole Requested': return 'bg-blue-100 text-blue-700 border-blue-200';
    default: return 'bg-slate-100 text-slate-700 border-slate-200';
  }
};

const getSubscriptionStatusColor = (status) => {
  switch (status) {
    case 'active': return 'bg-emerald-100 text-emerald-700';
    case 'paused': return 'bg-amber-100 text-amber-700';
    case 'canceled': return 'bg-red-100 text-red-700';
    case 'past_due': return 'bg-orange-100 text-orange-700';
    case 'trialing': return 'bg-blue-100 text-blue-700';
    default: return 'bg-slate-100 text-slate-700';
  }
};

export default function RentalDetailPage() {
  const { rentalId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [subscriptionAction, setSubscriptionAction] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Swap dialog state
  const [swapDialogOpen, setSwapDialogOpen] = useState(false);
  const [swapStep, setSwapStep] = useState(1); // Step 1: Select poles to remove, Step 2: Select new poles
  const [polesToRemove, setPolesToRemove] = useState([]); // Poles selected to be removed in swap
  const [availablePoles, setAvailablePoles] = useState([]);
  const [selectedPoleIds, setSelectedPoleIds] = useState([]);
  const [swapFeePerPole, setSwapFeePerPole] = useState(35);
  const [swapNotes, setSwapNotes] = useState("");
  const [swapping, setSwapping] = useState(false);
  const [poleSizes, setPoleSizes] = useState({ lengths: [], weights: [] });
  const [lengthFilter, setLengthFilter] = useState("");
  const [weightFilter, setWeightFilter] = useState("");
  const [swapSerialFilter, setSwapSerialFilter] = useState("");
  const [loadingPoles, setLoadingPoles] = useState(false);
  
  // Remove pole state
  const [removingPoleId, setRemovingPoleId] = useState(null);
  const [confirmRemovePole, setConfirmRemovePole] = useState(null);

  // Add pole state
  const [addPoleDialogOpen, setAddPoleDialogOpen] = useState(false);
  const [addPoleAvailable, setAddPoleAvailable] = useState([]);
  const [addPoleLoading, setAddPoleLoading] = useState(false);
  const [addPoleLengthFilter, setAddPoleLengthFilter] = useState("");
  const [addPoleWeightFilter, setAddPoleWeightFilter] = useState("");
  const [addPoleSerialFilter, setAddPoleSerialFilter] = useState("");
  const [selectedAddPoleId, setSelectedAddPoleId] = useState(null);
  const [addingPole, setAddingPole] = useState(false);
  
  // Proration state for adding poles
  const [showProrationDialog, setShowProrationDialog] = useState(false);
  const [prorationData, setProrationData] = useState(null);
  const [chargeProration, setChargeProration] = useState(true);
  const [prorationOverride, setProrationOverride] = useState("");
  const [loadingProration, setLoadingProration] = useState(false);

  // Start subscription state
  const [startSubDialogOpen, setStartSubDialogOpen] = useState(false);
  const [subPreview, setSubPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [includeInsurance, setIncludeInsurance] = useState(true);
  const [automaticTax, setAutomaticTax] = useState(true);  // Stripe automatic tax
  const [billingMethod, setBillingMethod] = useState('charge_automatically'); // 'charge_automatically' or 'send_invoice'
  const [startingSubscription, setStartingSubscription] = useState(false);

  // Pole history state
  const [poleHistory, setPoleHistory] = useState([]);
  const [loadingPoleHistory, setLoadingPoleHistory] = useState(false);

  useEffect(() => {
    if (rentalId) {
      fetchRentalDetails();
      fetchPoleHistory();
    }
  }, [rentalId]);
  
  const fetchPoleHistory = async () => {
    setLoadingPoleHistory(true);
    try {
      const response = await axios.get(`${API}/rentals/${rentalId}/pole-history`);
      setPoleHistory(response.data.history || []);
    } catch (err) {
      console.error('Error fetching pole history:', err);
    } finally {
      setLoadingPoleHistory(false);
    }
  };

  const fetchRentalDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/rentals/${rentalId}`);
      setData(response.data);
      setSelectedStatus(response.data.rental?.rental_status_id?.toString());
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatusId) => {
    if (!newStatusId || newStatusId === selectedStatus) return;
    
    setUpdatingStatus(true);
    try {
      await axios.put(`${API}/rentals/${rentalId}/status`, {
        status_id: parseInt(newStatusId)
      });
      toast.success("Status updated successfully");
      setSelectedStatus(newStatusId);
      fetchRentalDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleSubscriptionAction = async (subscriptionId, action) => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/subscriptions/${subscriptionId}/${action}`);
      toast.success(`Subscription ${action === 'cancel' ? 'cancelled' : action === 'pause' ? 'paused' : 'resumed'} successfully`);
      fetchRentalDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} subscription`);
    } finally {
      setActionLoading(false);
      setSubscriptionAction(null);
    }
  };

  // Swap rental functions
  const fetchPoleSizes = async () => {
    try {
      const response = await axios.get(`${API}/poles/sizes`);
      setPoleSizes(response.data || { lengths: [], weights: [] });
    } catch (err) {
      console.error("Failed to fetch pole sizes:", err);
    }
  };

  const fetchAvailablePoles = async (length = "", weight = "") => {
    setLoadingPoles(true);
    try {
      let url = `${API}/poles/available`;
      const params = new URLSearchParams();
      if (length) params.append('length', length);
      if (weight) params.append('weight', weight);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setAvailablePoles(response.data.poles || []);
    } catch (err) {
      console.error("Failed to fetch available poles:", err);
      toast.error("Failed to load available poles");
    } finally {
      setLoadingPoles(false);
    }
  };

  const openSwapDialog = () => {
    fetchPoleSizes();
    setSwapStep(1); // Start at step 1: select poles to remove
    setPolesToRemove([]);
    setSelectedPoleIds([]);
    setSwapFeePerPole(35);
    setSwapNotes("");
    setLengthFilter("");
    setWeightFilter("");
    setSwapSerialFilter("");
    setSwapDialogOpen(true);
  };

  const togglePoleToRemove = (poleId) => {
    setPolesToRemove(prev => 
      prev.includes(poleId) 
        ? prev.filter(id => id !== poleId)
        : [...prev, poleId]
    );
  };

  const togglePoleSelection = (poleId) => {
    setSelectedPoleIds(prev => 
      prev.includes(poleId) 
        ? prev.filter(id => id !== poleId)
        : [...prev, poleId]
    );
  };

  const goToSwapStep2 = () => {
    if (polesToRemove.length === 0) {
      toast.error("Please select at least one pole to swap out");
      return;
    }
    fetchAvailablePoles();
    setSwapStep(2);
  };

  const handleSwapRental = async () => {
    if (selectedPoleIds.length === 0) {
      toast.error("Please select at least one new pole");
      return;
    }

    setSwapping(true);
    try {
      const response = await axios.post(`${API}/rentals/${rentalId}/swap`, {
        new_pole_ids: selectedPoleIds,
        poles_to_remove: polesToRemove,
        swap_fee_per_pole: swapFeePerPole,
        notes: swapNotes || undefined
      });
      
      toast.success(response.data.message || "Poles swapped successfully!");
      setSwapDialogOpen(false);
      setSwapStep(1);
      
      // Refresh the rental details to show updated poles
      fetchRentalDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to swap rental");
    } finally {
      setSwapping(false);
    }
  };

  const calculateTotalSwapFee = () => {
    return polesToRemove.length * swapFeePerPole;
  };

  // Remove pole functions
  const handleRemovePole = async (poleId) => {
    setRemovingPoleId(poleId);
    try {
      const response = await axios.post(`${API}/rentals/${rentalId}/remove-pole`, {
        pole_id: poleId,
        adjust_subscription: true
      });
      
      toast.success(response.data.message);
      fetchRentalDetails(); // Refresh data
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove pole");
    } finally {
      setRemovingPoleId(null);
      setConfirmRemovePole(null);
    }
  };

  // Add pole functions
  const fetchAvailablePolesForAdd = async (length = "", weight = "") => {
    setAddPoleLoading(true);
    try {
      let url = `${API}/poles/available`;
      const params = new URLSearchParams();
      if (length) params.append('length', length);
      if (weight) params.append('weight', weight);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setAddPoleAvailable(response.data.poles || []);
    } catch (err) {
      console.error("Failed to fetch available poles:", err);
      toast.error("Failed to load available poles");
    } finally {
      setAddPoleLoading(false);
    }
  };

  const openAddPoleDialog = () => {
    fetchPoleSizes();
    fetchAvailablePolesForAdd();
    setSelectedAddPoleId(null);
    setAddPoleLengthFilter("");
    setAddPoleWeightFilter("");
    setAddPoleDialogOpen(true);
  };

  // Calculate proration when user wants to add a pole
  const calculateProration = async () => {
    if (!selectedAddPoleId) {
      toast.error("Please select a pole to add");
      return;
    }
    
    // Check if rental has an active subscription
    const activeSubscription = subscriptions?.find(s => s.status === 'active' || s.status === 'trialing');
    if (!activeSubscription) {
      // No active subscription - just add the pole directly
      await confirmAddPole(false, 0);
      return;
    }
    
    setLoadingProration(true);
    try {
      const response = await axios.get(`${API}/rentals/${rentalId}/calculate-proration?pole_id=${selectedAddPoleId}`);
      setProrationData(response.data);
      setProrationOverride(response.data.prorated_amount?.toFixed(2) || "0");
      setChargeProration(response.data.prorated_amount > 0);
      setShowProrationDialog(true);
    } catch (err) {
      toast.error("Failed to calculate proration - proceeding without");
      await confirmAddPole(false, 0);
    } finally {
      setLoadingProration(false);
    }
  };
  
  // Actually add the pole after proration decision
  const confirmAddPole = async (shouldChargeProration, overrideAmount) => {
    setAddingPole(true);
    try {
      const response = await axios.post(`${API}/rentals/${rentalId}/add-pole`, {
        pole_id: selectedAddPoleId,
        adjust_subscription: true,
        charge_prorated_fee: shouldChargeProration,
        prorated_amount_override: overrideAmount > 0 ? parseFloat(overrideAmount) : null
      });
      
      let message = response.data.message;
      if (response.data.subscription_updated) {
        message += " • Subscription updated";
      }
      if (response.data.prorated_charged && response.data.prorated_amount > 0) {
        message += ` • $${response.data.prorated_amount.toFixed(2)} charged`;
      }
      
      toast.success(message);
      setAddPoleDialogOpen(false);
      setShowProrationDialog(false);
      setSelectedAddPoleId(null);
      fetchRentalDetails(); // Refresh data
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add pole");
    } finally {
      setAddingPole(false);
    }
  };

  const handleAddPole = async () => {
    if (!selectedAddPoleId) {
      toast.error("Please select a pole to add");
      return;
    }
    
    // If rental has active subscription, calculate proration first
    const activeSubscription = subscriptions?.find(s => s.status === 'active' || s.status === 'trialing');
    if (activeSubscription) {
      await calculateProration();
    } else {
      // No subscription, just add the pole
      await confirmAddPole(false, 0);
    }
  };

  // Filter available poles for add dialog
  const filteredAddPoles = addPoleAvailable.filter(pole => {
    if (addPoleLengthFilter && pole.length !== addPoleLengthFilter) return false;
    if (addPoleWeightFilter && pole.weight !== addPoleWeightFilter) return false;
    if (addPoleSerialFilter) {
      const serialMatch = pole.serial_num?.toLowerCase().includes(addPoleSerialFilter.toLowerCase());
      const tagMatch = pole.tag_id?.toString().includes(addPoleSerialFilter);
      if (!serialMatch && !tagMatch) return false;
    }
    return true;
  });

  // Start Subscription functions
  const fetchSubscriptionPreview = async (insurance = true, autoTax = true) => {
    setLoadingPreview(true);
    try {
      const params = new URLSearchParams({
        include_insurance: insurance,
        automatic_tax: autoTax
      });
      const response = await axios.get(`${API}/rentals/${rentalId}/subscription-preview?${params}`);
      setSubPreview(response.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load subscription preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const openStartSubDialog = () => {
    // Default insurance checkbox to what was originally requested in the pole request
    const defaultInsurance = rental?.requested_insurance ?? true;
    setIncludeInsurance(defaultInsurance);
    setAutomaticTax(true);
    setBillingMethod('charge_automatically');
    setSubPreview(null);
    fetchSubscriptionPreview(defaultInsurance, true);
    setStartSubDialogOpen(true);
  };

  const handleInsuranceChange = (checked) => {
    setIncludeInsurance(checked);
    fetchSubscriptionPreview(checked, automaticTax);
  };

  const handleAutomaticTaxChange = (checked) => {
    setAutomaticTax(checked);
    fetchSubscriptionPreview(includeInsurance, checked);
  };

  const handleStartSubscription = async () => {
    setStartingSubscription(true);
    try {
      const response = await axios.post(`${API}/rentals/${rentalId}/start-subscription`, {
        include_insurance: includeInsurance,
        automatic_tax: automaticTax,
        collection_method: billingMethod
      });
      
      toast.success(response.data.message);
      setStartSubDialogOpen(false);
      fetchRentalDetails(); // Refresh to show new subscription
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to start subscription");
    } finally {
      setStartingSubscription(false);
    }
  };

  // Filter available poles for swap
  const filteredAvailablePoles = availablePoles.filter(pole => {
    if (lengthFilter && pole.length !== lengthFilter) return false;
    if (weightFilter && pole.weight !== weightFilter) return false;
    if (swapSerialFilter) {
      const serialMatch = pole.serial_num?.toLowerCase().includes(swapSerialFilter.toLowerCase());
      const tagMatch = pole.tag_id?.toString().includes(swapSerialFilter);
      if (!serialMatch && !tagMatch) return false;
    }
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-slate-600 mb-4">{error}</p>
        <div className="flex gap-2">
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />Go Back
          </Button>
          <Button onClick={fetchRentalDetails}>Retry</Button>
        </div>
      </div>
    );
  }

  const rental = data?.rental;
  const poles = data?.poles || [];
  const statuses = data?.statuses || [];
  const history = data?.history || [];
  const stripe = data?.stripe;
  const subscriptions = data?.subscriptions || [];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="rental-detail-page">
      {/* Subscription Action Confirmation Dialog */}
      <AlertDialog open={!!subscriptionAction} onOpenChange={() => setSubscriptionAction(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {subscriptionAction?.action === 'cancel' && 'Cancel Subscription'}
              {subscriptionAction?.action === 'pause' && 'Pause Subscription'}
              {subscriptionAction?.action === 'resume' && 'Resume Subscription'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {subscriptionAction?.action === 'cancel' && 'This will cancel the subscription at the end of the current billing period. The customer will not be charged again.'}
              {subscriptionAction?.action === 'pause' && 'This will pause the subscription. The customer will not be charged until the subscription is resumed.'}
              {subscriptionAction?.action === 'resume' && 'This will resume the subscription and billing will continue.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleSubscriptionAction(subscriptionAction.id, subscriptionAction.action)}
              disabled={actionLoading}
              className={subscriptionAction?.action === 'cancel' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Swap Rental Dialog - Two Step Process */}
      <Dialog open={swapDialogOpen} onOpenChange={(open) => { if (!open) { setSwapStep(1); } setSwapDialogOpen(open); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5" />
              Swap Rental - Step {swapStep} of 2
            </DialogTitle>
            <DialogDescription>
              {swapStep === 1 
                ? "Select which poles you want to remove from this rental."
                : "Select the new poles to add to this rental."
              }
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {swapStep === 1 ? (
              <>
                {/* Step 1: Select poles to remove */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
                  <strong>Step 1:</strong> Select the poles you want to swap out from this rental.
                </div>

                <div className="space-y-2">
                  <Label>Current Poles - Select to Remove ({polesToRemove.length} selected)</Label>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[40px]"></TableHead>
                          <TableHead>Serial Number</TableHead>
                          <TableHead>Length</TableHead>
                          <TableHead>Weight</TableHead>
                          <TableHead>Brand</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {poles.map(pole => (
                          <TableRow 
                            key={pole.id} 
                            className={`cursor-pointer hover:bg-slate-50 ${polesToRemove.includes(pole.id) ? 'bg-red-50' : ''}`}
                            onClick={() => togglePoleToRemove(pole.id)}
                          >
                            <TableCell>
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${polesToRemove.includes(pole.id) ? 'bg-red-600 border-red-600' : 'border-slate-300'}`}>
                                {polesToRemove.includes(pole.id) && <Check className="w-3 h-3 text-white" />}
                              </div>
                            </TableCell>
                            <TableCell className="font-mono font-medium">{pole.serial_num || pole.serial_number || '-'}</TableCell>
                            <TableCell>{pole.length}</TableCell>
                            <TableCell>{pole.weight}</TableCell>
                            <TableCell>{pole.brand || '-'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {polesToRemove.length > 0 && (
                  <div className="bg-red-50 rounded-lg p-3">
                    <div className="text-sm font-medium text-red-700 mb-2">Poles to Remove:</div>
                    <div className="flex flex-wrap gap-2">
                      {polesToRemove.map(id => {
                        const pole = poles.find(p => p.id === id);
                        return pole ? (
                          <Badge key={id} className="bg-red-100 text-red-800 font-mono text-xs">
                            {pole.pole_code} | {pole.length} - {pole.weight}
                            <button 
                              className="ml-1 hover:text-red-900" 
                              onClick={(e) => { e.stopPropagation(); togglePoleToRemove(id); }}
                            >
                              ×
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {/* Swap Fee */}
                <div className="space-y-2">
                  <Label>Swap Fee Per Pole ($)</Label>
                  <Input
                    type="number"
                    value={swapFeePerPole}
                    onChange={(e) => setSwapFeePerPole(parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                  />
                  <div className="text-sm text-slate-500">
                    Total swap fee: <span className="font-semibold text-slate-900">${calculateTotalSwapFee().toFixed(2)}</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Step 2: Select new poles */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  <strong>Step 2:</strong> Select the new poles to add to this rental.
                </div>

                {/* Summary of what's being removed */}
                <div className="bg-red-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-red-700 mb-1">Removing {polesToRemove.length} pole(s):</div>
                  <div className="flex flex-wrap gap-1">
                    {polesToRemove.map(id => {
                      const pole = poles.find(p => p.id === id);
                      return pole ? (
                        <Badge key={id} variant="outline" className="text-xs font-mono">
                          {pole.pole_code}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Filter by Length</Label>
                    <Select value={lengthFilter || "all"} onValueChange={(v) => setLengthFilter(v === "all" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Lengths" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Lengths</SelectItem>
                        {poleSizes.lengths.map(length => (
                          <SelectItem key={length} value={length}>{length}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Filter by Weight</Label>
                    <Select value={weightFilter || "all"} onValueChange={(v) => setWeightFilter(v === "all" ? "" : v)}>
                      <SelectTrigger>
                        <SelectValue placeholder="All Weights" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Weights</SelectItem>
                        {poleSizes.weights.map(weight => (
                          <SelectItem key={weight} value={weight}>{weight} lbs</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Search Serial/Tag</Label>
                    <Input
                      placeholder="Enter serial # or tag..."
                      value={swapSerialFilter}
                      onChange={(e) => setSwapSerialFilter(e.target.value)}
                      className="h-10"
                    />
                  </div>
                </div>

                {/* Available Poles Selection */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Select New Poles ({selectedPoleIds.length} selected)</Label>
                    <Button variant="ghost" size="sm" onClick={() => fetchAvailablePoles(lengthFilter, weightFilter)}>
                      <RefreshCw className="w-3 h-3 mr-1" />Refresh
                    </Button>
                  </div>
                  
                  {loadingPoles ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                    </div>
                  ) : filteredAvailablePoles.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      No available poles found
                    </div>
                  ) : (
                    <ScrollArea className="h-[200px] border rounded-lg">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[40px]"></TableHead>
                            <TableHead>Serial Number</TableHead>
                            <TableHead>Length</TableHead>
                            <TableHead>Weight</TableHead>
                            <TableHead>Brand</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredAvailablePoles.map(pole => (
                            <TableRow 
                              key={pole.id} 
                              className={`cursor-pointer hover:bg-slate-50 ${selectedPoleIds.includes(pole.id) ? 'bg-emerald-50' : ''}`}
                              onClick={() => togglePoleSelection(pole.id)}
                            >
                              <TableCell>
                                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${selectedPoleIds.includes(pole.id) ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300'}`}>
                                  {selectedPoleIds.includes(pole.id) && <Check className="w-3 h-3 text-white" />}
                                </div>
                              </TableCell>
                              <TableCell className="font-mono font-medium">{pole.serial_num || '-'}</TableCell>
                              <TableCell>{pole.length}</TableCell>
                              <TableCell>{pole.weight}</TableCell>
                              <TableCell>{pole.brand || '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </ScrollArea>
                  )}
                </div>

                {/* Selected New Poles Summary */}
                {selectedPoleIds.length > 0 && (
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <div className="text-sm font-medium text-emerald-700 mb-2">New Poles to Add:</div>
                    <div className="flex flex-wrap gap-2">
                      {selectedPoleIds.map(id => {
                        const pole = availablePoles.find(p => p.id === id);
                        return pole ? (
                          <Badge key={id} className="bg-emerald-100 text-emerald-800 font-mono text-xs">
                            {pole.pole_code} | {pole.length} - {pole.weight}
                            <button 
                              className="ml-1 hover:text-red-600" 
                              onClick={(e) => { e.stopPropagation(); togglePoleSelection(id); }}
                            >
                              ×
                            </button>
                          </Badge>
                        ) : null;
                      })}
                    </div>
                  </div>
                )}

                {/* Notes */}
                <div className="space-y-2">
                  <Label>Notes (Optional)</Label>
                  <Input
                    placeholder="Reason for swap..."
                    value={swapNotes}
                    onChange={(e) => setSwapNotes(e.target.value)}
                  />
                </div>

                {/* Summary */}
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                  <div className="font-medium text-amber-800 mb-1">This action will:</div>
                  <ul className="list-disc list-inside text-amber-700 space-y-1">
                    <li>Remove {polesToRemove.length} pole(s) from rental #{rentalId}</li>
                    <li>Add {selectedPoleIds.length} new pole(s) to the rental</li>
                    <li>Charge ${calculateTotalSwapFee().toFixed(2)} swap fee</li>
                    <li>Adjust the subscription accordingly</li>
                  </ul>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            {swapStep === 1 ? (
              <>
                <Button variant="outline" onClick={() => setSwapDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={goToSwapStep2} disabled={polesToRemove.length === 0}>
                  Continue to Select New Poles
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => setSwapStep(1)}>
                  Back
                </Button>
                <Button onClick={handleSwapRental} disabled={swapping || selectedPoleIds.length === 0}>
                  {swapping && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Complete Swap
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Remove Pole Dialog */}
      <AlertDialog open={!!confirmRemovePole} onOpenChange={() => setConfirmRemovePole(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Pole from Rental?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove the pole from rental #{rentalId} and adjust the subscription accordingly.
              {confirmRemovePole && (
                <div className="mt-2 p-2 bg-slate-100 rounded font-mono text-sm">
                  {confirmRemovePole.pole_code} | {confirmRemovePole.length} - {confirmRemovePole.weight}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={!!removingPoleId}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleRemovePole(confirmRemovePole?.pole_id)}
              disabled={!!removingPoleId}
              className="bg-red-600 hover:bg-red-700"
            >
              {removingPoleId && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Remove Pole
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Pole Dialog */}
      <Dialog open={addPoleDialogOpen} onOpenChange={setAddPoleDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-600" />
              Add Pole to Rental
            </DialogTitle>
            <DialogDescription>
              Select an available pole to add to rental #{rentalId}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Filters */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Filter by Length</Label>
                <Select 
                  value={addPoleLengthFilter || "all"} 
                  onValueChange={(v) => {
                    setAddPoleLengthFilter(v === "all" ? "" : v);
                    fetchAvailablePolesForAdd(v === "all" ? "" : v, addPoleWeightFilter);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Lengths" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Lengths</SelectItem>
                    {poleSizes.lengths.map(length => (
                      <SelectItem key={length} value={length}>{length}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Filter by Weight</Label>
                <Select 
                  value={addPoleWeightFilter || "all"} 
                  onValueChange={(v) => {
                    setAddPoleWeightFilter(v === "all" ? "" : v);
                    fetchAvailablePolesForAdd(addPoleLengthFilter, v === "all" ? "" : v);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Weights" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Weights</SelectItem>
                    {poleSizes.weights.map(weight => (
                      <SelectItem key={weight} value={weight}>{weight} lbs</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Search Serial/Tag</Label>
                <Input
                  placeholder="Enter serial # or tag..."
                  value={addPoleSerialFilter}
                  onChange={(e) => setAddPoleSerialFilter(e.target.value)}
                  className="h-10"
                />
              </div>
            </div>

            {/* Available Poles List */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Select Pole ({filteredAddPoles.length} available)</Label>
                <Button variant="ghost" size="sm" onClick={() => fetchAvailablePolesForAdd(addPoleLengthFilter, addPoleWeightFilter)}>
                  <RefreshCw className="w-3 h-3 mr-1" />Refresh
                </Button>
              </div>
              
              {addPoleLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                </div>
              ) : filteredAddPoles.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  No available poles found
                </div>
              ) : (
                <ScrollArea className="h-[250px] border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[40px]"></TableHead>
                        <TableHead>Serial Number</TableHead>
                        <TableHead>Length</TableHead>
                        <TableHead>Weight</TableHead>
                        <TableHead>Brand</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAddPoles.map(pole => (
                        <TableRow 
                          key={pole.id} 
                          className={`cursor-pointer hover:bg-slate-50 ${selectedAddPoleId === pole.id ? 'bg-emerald-50' : ''}`}
                          onClick={() => setSelectedAddPoleId(pole.id)}
                        >
                          <TableCell>
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedAddPoleId === pole.id ? 'bg-emerald-600 border-emerald-600' : 'border-slate-300'}`}>
                              {selectedAddPoleId === pole.id && <Check className="w-3 h-3 text-white" />}
                            </div>
                          </TableCell>
                          <TableCell className="font-mono font-medium">{pole.serial_num || '-'}</TableCell>
                          <TableCell>{pole.length}</TableCell>
                          <TableCell>{pole.weight}</TableCell>
                          <TableCell>{pole.brand || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </div>

            {/* Selected Pole Info */}
            {selectedAddPoleId && (
              <div className="bg-emerald-50 rounded-lg p-3">
                <div className="text-sm font-medium text-emerald-700 mb-1">Selected Pole:</div>
                {(() => {
                  const pole = filteredAddPoles.find(p => p.id === selectedAddPoleId);
                  return pole ? (
                    <div className="flex items-center gap-3">
                      <Badge className="bg-emerald-100 text-emerald-800 font-mono">{pole.pole_code}</Badge>
                      <span className="text-sm text-emerald-700">{pole.length} - {pole.weight}</span>
                    </div>
                  ) : null;
                })()}
              </div>
            )}

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
              <strong>Note:</strong> Adding a pole will automatically adjust the subscription (if active) to include the new pole.
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddPoleDialogOpen(false)} disabled={addingPole}>
              Cancel
            </Button>
            <Button 
              onClick={handleAddPole} 
              disabled={addingPole || !selectedAddPoleId || loadingProration}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-add-pole"
            >
              {(addingPole || loadingProration) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Add Pole
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proration Dialog - shown when adding pole to rental with active subscription */}
      <Dialog open={showProrationDialog} onOpenChange={setShowProrationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-600" />
              Prorated Fee
            </DialogTitle>
            <DialogDescription>
              Add {prorationData?.pole_length} - {prorationData?.pole_weight} to the subscription
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {prorationData?.has_subscription ? (
              <>
                {/* Subscription Info */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Monthly Price:</span>
                    <span className="font-mono font-medium">${prorationData?.monthly_price?.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Days Until Renewal:</span>
                    <span className="font-medium">{prorationData?.days_remaining} days</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Renewal Date:</span>
                    <span className="font-medium">
                      {prorationData?.renewal_date ? new Date(prorationData.renewal_date).toLocaleDateString() : '-'}
                    </span>
                  </div>
                  <div className="border-t pt-2 mt-2">
                    <div className="flex justify-between font-medium">
                      <span>Calculated Proration:</span>
                      <span className="text-amber-600">${prorationData?.prorated_amount?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Charge Proration Checkbox */}
                <div className="flex items-start space-x-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <Checkbox 
                    id="chargeProration"
                    checked={chargeProration}
                    onCheckedChange={setChargeProration}
                  />
                  <div className="flex-1">
                    <Label htmlFor="chargeProration" className="text-sm font-medium cursor-pointer">
                      Charge prorated fee now
                    </Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {chargeProration 
                        ? "Fee will be charged immediately to customer's card"
                        : "No prorated fee will be charged - subscription starts at next renewal"
                      }
                    </p>
                  </div>
                </div>

                {/* Override Amount */}
                {chargeProration && (
                  <div className="space-y-2">
                    <Label>Prorated Amount to Charge</Label>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-medium text-slate-500">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={prorationOverride}
                        onChange={(e) => setProrationOverride(e.target.value)}
                        className="w-32 font-mono"
                      />
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setProrationOverride(prorationData?.prorated_amount?.toFixed(2) || "0")}
                      >
                        Reset
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500">
                      You can override the calculated amount if needed
                    </p>
                  </div>
                )}

                {/* Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  <strong>Going forward:</strong> The subscription will include this pole at ${prorationData?.monthly_price?.toFixed(2)}/month starting from the next billing cycle.
                </div>
              </>
            ) : (
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-slate-600">{prorationData?.message || "No active subscription"}</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowProrationDialog(false)} disabled={addingPole}>
              Cancel
            </Button>
            <Button 
              onClick={() => confirmAddPole(chargeProration, chargeProration ? parseFloat(prorationOverride) || 0 : 0)}
              disabled={addingPole}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {addingPole && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {chargeProration && parseFloat(prorationOverride) > 0 
                ? `Add Pole & Charge $${parseFloat(prorationOverride).toFixed(2)}`
                : "Add Pole"
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Start Subscription Dialog */}
      <Dialog open={startSubDialogOpen} onOpenChange={setStartSubDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Zap className="w-5 h-5 text-emerald-600" />
              Start Subscription
            </DialogTitle>
            <DialogDescription>
              Create a recurring subscription for rental #{rentalId}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {loadingPreview ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : subPreview ? (
              <>
                {/* Poles Summary */}
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Poles in Rental ({subPreview.poles?.length || 0})
                  </div>
                  <div className="space-y-1">
                    {subPreview.poles?.map(pole => (
                      <div key={pole.id} className="flex justify-between text-sm">
                        <span className="font-mono">{pole.pole_code}</span>
                        <span className="text-slate-500">{pole.length} - {pole.weight}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Options */}
                <div className="space-y-3">
                  {/* Insurance Option */}
                  <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <div>
                        <div className="font-medium text-sm">Include Insurance</div>
                        <div className="text-xs text-slate-500">$10.00 per pole/month</div>
                      </div>
                    </div>
                    <Checkbox 
                      checked={includeInsurance} 
                      onCheckedChange={handleInsuranceChange}
                      data-testid="include-insurance-checkbox"
                    />
                  </div>

                  {/* Tax Option */}
                  {/* Automatic Tax Option */}
                  <div className="flex items-center justify-between p-3 border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-3">
                      <Receipt className="w-5 h-5 text-amber-500" />
                      <div>
                        <div className="font-medium text-sm">Automatic Tax</div>
                        <div className="text-xs text-slate-500">Calculated by Stripe based on customer address</div>
                      </div>
                    </div>
                    <Checkbox 
                      checked={automaticTax} 
                      onCheckedChange={handleAutomaticTaxChange}
                      data-testid="automatic-tax-checkbox"
                    />
                  </div>

                  {/* Billing Method Option */}
                  <div className="p-3 border border-slate-200 rounded-lg">
                    <div className="flex items-center gap-3 mb-2">
                      <CreditCard className="w-5 h-5 text-slate-500" />
                      <div className="font-medium text-sm">Billing Method</div>
                    </div>
                    <Select value={billingMethod} onValueChange={setBillingMethod}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="charge_automatically">
                          <div className="flex flex-col">
                            <span>Charge card on file</span>
                            <span className="text-xs text-slate-500">Auto-bill each month</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="send_invoice">
                          <div className="flex flex-col">
                            <span>Send invoice</span>
                            <span className="text-xs text-slate-500">Customer pays manually</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Price Breakdown */}
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-3 py-2 font-medium text-sm">
                    Monthly Price Breakdown
                  </div>
                  <div className="p-3 space-y-2">
                    {subPreview.pricing?.breakdown?.filter(item => item.type !== 'tax' || item.note !== 'Automatic').map((item, idx) => (
                      <div key={idx} className="flex justify-between text-sm">
                        <span className={item.type === 'tax' ? 'text-slate-500 italic' : ''}>
                          {item.description}
                          {item.quantity > 1 && item.type !== 'tax' && ` (×${item.quantity})`}
                        </span>
                        <span className={item.type === 'tax' ? 'text-slate-500 italic' : 'font-medium'}>
                          {item.type === 'tax' && item.note === 'Automatic' ? 'Auto' : `$${item.subtotal?.toFixed(2)}`}
                        </span>
                      </div>
                    ))}
                    {automaticTax && (
                      <div className="flex justify-between text-sm text-slate-500 italic">
                        <span>Tax (based on address)</span>
                        <span>Calculated at checkout</span>
                      </div>
                    )}
                    <div className="border-t pt-2 mt-2 flex justify-between font-semibold">
                      <span>Subtotal</span>
                      <span className="text-emerald-600" data-testid="total-monthly-price">
                        ${subPreview.pricing?.total_monthly?.toFixed(2)}
                      </span>
                    </div>
                    {automaticTax && (
                      <div className="text-xs text-slate-500 text-right">
                        + applicable tax
                      </div>
                    )}
                  </div>
                </div>

                {/* Info Note */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                  <strong>Note:</strong> Starting this subscription will set the rental status to Active and begin monthly billing.
                  {automaticTax && " Tax will be calculated automatically by Stripe based on the customer's address."}
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                Failed to load subscription preview
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStartSubDialogOpen(false)} disabled={startingSubscription}>
              Cancel
            </Button>
            <Button 
              onClick={handleStartSubscription} 
              disabled={startingSubscription || !subPreview}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-start-subscription"
            >
              {startingSubscription && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Start Subscription
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Header - Mobile optimized */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="h-8 px-2 sm:px-3" data-testid="back-button">
                <ArrowLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div>
                <h1 className="text-lg sm:text-xl font-semibold text-slate-900" data-testid="rental-title">
                  Rental #{rental?.rental_id}
                </h1>
                <p className="text-xs sm:text-sm text-slate-500 truncate max-w-[180px] sm:max-w-none">
                  {rental?.full_name || `User #${rental?.user_id}`}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 flex-wrap justify-end">
              {/* Start Subscription Button - only show if no active/trialing subscription exists */}
              {(() => {
                const hasActiveSubscription = subscriptions.some(sub => 
                  sub.status === 'active' || sub.status === 'trialing'
                );
                const canShowStartButton = !hasActiveSubscription && 
                  rental?.status_name !== 'Completed' && 
                  rental?.status_name !== 'Cancelled';
                
                if (canShowStartButton) {
                  return (
                    <Button 
                      variant="default" 
                      size="sm" 
                      onClick={openStartSubDialog}
                      className="bg-emerald-600 hover:bg-emerald-700 h-8 px-2 sm:px-3 text-xs sm:text-sm"
                      data-testid="start-subscription-button"
                    >
                      <Zap className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Start Subscription</span>
                      <span className="sm:hidden">Subscribe</span>
                    </Button>
                  );
                } else if (hasActiveSubscription) {
                  return (
                    <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 sm:px-3 py-1 text-xs">
                      <Zap className="w-3 h-3 mr-1 inline" />
                      <span className="hidden sm:inline">Subscription Active</span>
                      <span className="sm:hidden">Active</span>
                    </Badge>
                  );
                }
                return null;
              })()}
              {subscriptions.some(sub => sub.status === 'active' || sub.status === 'trialing') && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 px-2 sm:px-3 text-xs sm:text-sm"
                  data-testid="resync-subscription-button"
                  onClick={async () => {
                    try {
                      const res = await axios.post(`${API}/rentals/${rentalId}/resync-subscription`);
                      if (res.data.changes?.length > 0) {
                        toast.success(`Subscription resynced: ${res.data.changes.join(', ')}`);
                      } else {
                        toast.success('Subscription is already in sync!');
                      }
                      fetchRentalDetails();
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to resync subscription');
                    }
                  }}
                >
                  <RefreshCw className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">Resync Billing</span>
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={openSwapDialog}
                disabled={rental?.status_name === 'Completed' || rental?.status_name === 'Cancelled'}
                className="h-8 px-2 sm:px-3 text-xs sm:text-sm"
                data-testid="swap-rental-button"
              >
                <ArrowRightLeft className="w-4 h-4 sm:mr-2" />
                <span className="hidden sm:inline">Swap Rental</span>
                <span className="sm:hidden">Swap</span>
              </Button>
              <Badge className={`${getStatusColor(rental?.status_name)} border text-xs`} data-testid="rental-status-badge">
                {rental?.status_name}
              </Badge>
              <Button variant="outline" size="sm" onClick={fetchRentalDetails} className="h-8 w-8 p-0" data-testid="refresh-button">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content - Single View - Mobile optimized */}
      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6">
        
        {/* Row 1: Overview + Status */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Renter Information */}
          <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-slate-500" />
              Renter Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-slate-400" />
                  <div>
                    <div className="text-xs text-slate-500">Full Name</div>
                    <div className="font-medium" data-testid="renter-name">
                      {rental?.full_name || '-'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <div>
                    <div className="text-xs text-slate-500">Email</div>
                    <div className="font-medium" data-testid="renter-email">
                      {rental?.email || '-'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-slate-400" />
                  <div>
                    <div className="text-xs text-slate-500">Phone</div>
                    <div className="font-medium">{rental?.phone || '-'}</div>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <School className="w-4 h-4 text-slate-400" />
                  <div>
                    <div className="text-xs text-slate-500">School / Club</div>
                    <div className="font-medium">{rental?.school || rental?.club || '-'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-slate-400" />
                  <div>
                    <div className="text-xs text-slate-500">Coach</div>
                    <div className="font-medium">{rental?.coach_name || '-'}</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-slate-400" />
                  <div>
                    <div className="text-xs text-slate-500">Guardian</div>
                    <div className="font-medium">
                      {rental?.guardian_name || '-'}
                      {rental?.guardian_phone && ` (${rental.guardian_phone})`}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {rental?.user_id && (
              <div className="mt-4 pt-4 border-t border-slate-100">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate(`/users/${rental.user_id}`)}
                  data-testid="view-user-profile"
                >
                  View Full User Profile
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </div>
            )}
          </div>

          {/* Status & Dates Card */}
          <div className="space-y-4">
            {/* Status Card */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <h3 className="font-medium text-slate-700 mb-3">Rental Status</h3>
              <Select
                value={selectedStatus}
                onValueChange={handleStatusChange}
                disabled={updatingStatus}
              >
                <SelectTrigger className="w-full" data-testid="status-select">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map(status => (
                    <SelectItem key={status.id} value={status.id.toString()}>
                      {status.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {updatingStatus && (
                <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                  <Loader2 className="w-3 h-3 animate-spin" />Updating...
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="grid grid-cols-2 gap-3 text-center">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-slate-900" data-testid="poles-count">{poles.length}</div>
                  <div className="text-xs text-slate-500">Poles</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-2xl font-bold text-emerald-600" data-testid="total-charged">
                    ${stripe?.total_charged?.toFixed(2) || '0.00'}
                  </div>
                  <div className="text-xs text-slate-500">Total Charged</div>
                </div>
              </div>
            </div>

            {/* Date Info */}
            <div className="bg-white rounded-lg border border-slate-200 p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Start Date</span>
                <span className="font-medium" data-testid="start-date">
                  {formatEST(rental?.start_date, 'MMM d, yyyy')}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Return Date</span>
                <span className="font-medium" data-testid="return-date">
                  {rental?.return_date ? formatEST(rental.return_date, 'MMM d, yyyy') : 'Not set'}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-500">Created</span>
                <span className="font-medium">{formatEST(rental?.created_at, 'MMM d, yyyy')}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Subscriptions */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-slate-500" />
            Subscriptions
            {stripe?.customer_id && (
              <a
                href={`https://dashboard.stripe.com/customers/${stripe.customer_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="ml-auto text-blue-600 hover:underline text-sm flex items-center gap-1"
              >
                Open Stripe Dashboard
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </h3>
          
          {subscriptions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CreditCard className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>No active subscriptions</p>
            </div>
          ) : (
            <div className="space-y-3">
              {subscriptions.map(sub => (
                <div key={sub.id} className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-slate-100 rounded-full p-2">
                        <DollarSign className="w-5 h-5 text-slate-600" />
                      </div>
                      <div>
                        <div className="font-semibold text-lg">
                          ${sub.items?.reduce((total, item) => total + (item.amount * (item.quantity || 1)), 0)?.toFixed(2) || '0.00'}/{sub.items?.[0]?.interval || 'month'}
                        </div>
                        <div className="text-xs text-slate-400 mb-1">
                          {sub.items?.map((item, idx) => (
                            <span key={item.price_id}>
                              {idx > 0 && ' + '}
                              {item.quantity || 1}x ${item.amount?.toFixed(2)}
                            </span>
                          ))}
                        </div>
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          Next billing: {formatUnixTimestamp(sub.current_period_end)}
                        </div>
                        {sub.cancel_at_period_end && (
                          <div className="text-sm text-amber-600 mt-1">
                            Cancels at end of period
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getSubscriptionStatusColor(sub.status)}>
                        {sub.status}
                      </Badge>
                      
                      {/* Subscription Actions */}
                      <div className="flex gap-2">
                        {sub.status === 'active' && !sub.cancel_at_period_end && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setSubscriptionAction({ id: sub.id, action: 'pause' })}
                              data-testid={`pause-sub-${sub.id}`}
                            >
                              <Pause className="w-4 h-4 mr-1" />
                              Pause
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={() => setSubscriptionAction({ id: sub.id, action: 'cancel' })}
                              data-testid={`cancel-sub-${sub.id}`}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          </>
                        )}
                        {sub.status === 'paused' && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={() => setSubscriptionAction({ id: sub.id, action: 'resume' })}
                            data-testid={`resume-sub-${sub.id}`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Resume
                          </Button>
                        )}
                        {sub.cancel_at_period_end && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            onClick={() => setSubscriptionAction({ id: sub.id, action: 'resume' })}
                            data-testid={`reactivate-sub-${sub.id}`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Reactivate
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Row 3: Poles */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
              <Package className="w-5 h-5 text-slate-500" />
              Poles ({poles.length})
            </h3>
            {rental?.status_name !== 'Completed' && rental?.status_name !== 'Cancelled' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={openAddPoleDialog}
                className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                data-testid="add-pole-button"
              >
                <Plus className="w-4 h-4 mr-1" />
                Add Pole
              </Button>
            )}
          </div>
          {poles.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>No poles associated with this rental</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Pole Code</TableHead>
                  <TableHead>Length</TableHead>
                  <TableHead>Weight</TableHead>
                  <TableHead>Brand</TableHead>
                  <TableHead>Serial #</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {poles.map(pole => (
                  <TableRow key={pole.id} data-testid={`pole-row-${pole.id}`}>
                    <TableCell className="font-medium font-mono">
                      <a 
                        href={`/poles?search=${pole.pole_code || pole.serial_number || pole.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline"
                      >
                        {pole.pole_code || `#${pole.id}`}
                      </a>
                    </TableCell>
                    <TableCell>
                      <a 
                        href={`/poles?search=${pole.length}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-slate-700 hover:text-blue-600 hover:underline"
                      >
                        {pole.length || '-'}
                      </a>
                    </TableCell>
                    <TableCell>{pole.weight || '-'}</TableCell>
                    <TableCell>{pole.brand || '-'}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {pole.serial_number ? (
                        <a 
                          href={`/poles?search=${pole.serial_number}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-slate-600 hover:text-blue-600 hover:underline"
                        >
                          {pole.serial_number}
                        </a>
                      ) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={pole.pole_status === 'active' ? 'text-emerald-600' : 'text-slate-500'}>
                        {pole.pole_status || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {poles.length > 1 && rental?.status_name !== 'Completed' && rental?.status_name !== 'Cancelled' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setConfirmRemovePole({
                            pole_id: pole.id,
                            pole_code: pole.pole_code,
                            length: pole.length,
                            weight: pole.weight
                          })}
                          disabled={removingPoleId === pole.id}
                          data-testid={`remove-pole-${pole.id}`}
                        >
                          {removingPoleId === pole.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Row 4: Payment History */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-slate-500" />
            Payment History
            {stripe && (
              <div className="ml-auto flex items-center gap-4">
                <div className="text-sm">
                  <span className="text-slate-500">Total: </span>
                  <span className="font-bold text-emerald-600">${stripe.total_charged?.toFixed(2)}</span>
                  {stripe.total_refunded > 0 && (
                    <span className="text-red-600 ml-2">(-${stripe.total_refunded?.toFixed(2)} refunded)</span>
                  )}
                </div>
                {stripe?.customer_id && (
                  <a
                    href={`https://dashboard.stripe.com/customers/${stripe.customer_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                    data-testid="stripe-dashboard-link"
                  >
                    Open Stripe
                    <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            )}
          </h3>
          
          {!stripe || stripe.charges?.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <CreditCard className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>No payment history found</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[300px]">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stripe.charges.map(charge => (
                    <TableRow key={charge.id} data-testid={`charge-row-${charge.id}`}>
                      <TableCell className="text-sm">
                        {formatUnixTimestamp(charge.created)}
                      </TableCell>
                      <TableCell className="font-medium">
                        ${charge.amount?.toFixed(2)} {charge.currency}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          charge.status === 'succeeded' ? 'bg-emerald-100 text-emerald-700' :
                          charge.status === 'failed' ? 'bg-red-100 text-red-700' :
                          'bg-slate-100 text-slate-700'
                        }>
                          {charge.refunded ? 'Refunded' : charge.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 max-w-[200px] truncate">
                        {charge.description || '-'}
                      </TableCell>
                      <TableCell className="text-sm">
                        {charge.card_brand && charge.card_last4 ? (
                          <span className="capitalize">{charge.card_brand} **** {charge.card_last4}</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {charge.receipt_url && (
                          <a
                            href={charge.receipt_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-sm"
                          >
                            Receipt
                          </a>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </div>

        {/* Row 5: Pickup/Return History */}
        <div className="bg-white rounded-lg border border-slate-200 p-6">
          <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <History className="w-5 h-5 text-slate-500" />
            Pickup / Return History
          </h3>
          
          {history.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <History className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>No pickup/return history</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map(item => {
                const isSwap = item.swap;
                const isCheckout = item.transaction_type === 0;
                
                return (
                  <div 
                    key={item.id} 
                    className={`border rounded-lg p-4 ${
                      isSwap ? 'border-purple-200 bg-purple-50/30' : 
                      isCheckout ? 'border-emerald-200 bg-emerald-50/30' : 
                      'border-amber-200 bg-amber-50/30'
                    }`}
                    data-testid={`history-row-${item.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        {isSwap ? (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-purple-100 rounded-full">
                            <Repeat className="w-4 h-4 text-purple-600" />
                            <span className="text-sm font-semibold text-purple-700">Swap</span>
                          </div>
                        ) : isCheckout ? (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-100 rounded-full">
                            <ArrowUpFromLine className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm font-semibold text-emerald-700">Pickup</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-amber-100 rounded-full">
                            <ArrowDownToLine className="w-4 h-4 text-amber-600" />
                            <span className="text-sm font-semibold text-amber-700">Return</span>
                          </div>
                        )}
                        <Badge variant="outline" className={
                          item.processing_status === 'completed' ? 'text-emerald-600 border-emerald-200' : 
                          item.processed ? 'text-emerald-600 border-emerald-200' : 
                          'text-amber-600 border-amber-200'
                        }>
                          {item.processing_status === 'completed' ? 'Processed' : item.processed ? 'Processed' : 'Pending'}
                        </Badge>
                      </div>
                      <span className="text-xs text-slate-500">{formatEST(item.created_at)}</span>
                    </div>

                    {isSwap ? (
                      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] gap-2 items-center">
                        {/* Pole returned (out) */}
                        <div className="bg-white rounded-md border border-red-100 p-3">
                          <div className="text-[10px] uppercase tracking-wider text-red-500 font-semibold mb-1 flex items-center gap-1">
                            <ArrowDownToLine className="w-3 h-3" /> Returned
                          </div>
                          <div className="font-mono font-semibold text-sm">{item.length} - {item.weight}</div>
                          <div className="text-xs text-slate-500 space-x-2">
                            {item.pole_code && <span>#{item.pole_code}</span>}
                            {item.serial_num && <span className="text-slate-400">SN: {item.serial_num}</span>}
                          </div>
                        </div>
                        
                        <div className="flex justify-center">
                          <ArrowRight className="w-5 h-5 text-purple-400 hidden sm:block" />
                          <ChevronDown className="w-5 h-5 text-purple-400 sm:hidden" />
                        </div>
                        
                        {/* Pole picked up (in) */}
                        <div className="bg-white rounded-md border border-emerald-100 p-3">
                          <div className="text-[10px] uppercase tracking-wider text-emerald-500 font-semibold mb-1 flex items-center gap-1">
                            <ArrowUpFromLine className="w-3 h-3" /> Picked Up
                          </div>
                          <div className="font-mono font-semibold text-sm">
                            {item.swap_length || '—'} - {item.swap_weight || '—'}
                          </div>
                          <div className="text-xs text-slate-500 space-x-2">
                            {item.swap_pole_code && <span>#{item.swap_pole_code}</span>}
                            {item.swap_serial_num && <span className="text-slate-400">SN: {item.swap_serial_num}</span>}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-white rounded-md border border-slate-100 p-3 inline-block">
                        <div className="font-mono font-semibold text-sm">
                          {item.length} - {item.weight}
                        </div>
                        <div className="text-xs text-slate-500 space-x-2">
                          {item.pole_code && <span>#{item.pole_code}</span>}
                          {item.serial_num && <span className="text-slate-400">SN: {item.serial_num}</span>}
                        </div>
                      </div>
                    )}

                    {item.notes && (
                      <div className="mt-2 text-sm text-slate-600 italic">
                        {item.notes}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Row 6: Pole History */}
        <div className="bg-white rounded-lg border border-slate-200 p-6" data-testid="pole-history-section">
          <h3 className="text-lg font-bold flex items-center gap-2 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            <Package className="w-5 h-5 text-slate-500" />
            Pole History
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={fetchPoleHistory}
              className="ml-auto"
            >
              <RefreshCw className={`w-4 h-4 ${loadingPoleHistory ? 'animate-spin' : ''}`} />
            </Button>
          </h3>
          
          {loadingPoleHistory ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : poleHistory.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Package className="w-10 h-10 mx-auto mb-2 text-slate-300" />
              <p>No pole history recorded</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Pole</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {poleHistory.map(entry => (
                  <TableRow key={entry.id} data-testid={`pole-history-row-${entry.id}`}>
                    <TableCell className="text-sm">
                      {formatEST(entry.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {entry.action === 'added' ? (
                          <Plus className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <Trash2 className="w-4 h-4 text-red-500" />
                        )}
                        <Badge className={entry.action === 'added' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                          {entry.action === 'added' ? 'Added' : 'Removed'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm font-mono">
                      <div>
                        {entry.length} - {entry.weight}
                        {entry.serial_num && (
                          <span className="text-slate-400 ml-2">SN: {entry.serial_num}</span>
                        )}
                      </div>
                      {entry.brand && <div className="text-xs text-slate-400">{entry.brand}</div>}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {entry.action_type === 'checkout' ? 'Checkout' : 
                         entry.action_type === 'return' ? 'Return' :
                         entry.action_type === 'swap' ? 'Swap' :
                         entry.action_type === 'manual_addition' ? 'Manual' :
                         entry.action_type === 'manual_removal' ? 'Manual' :
                         entry.action_type || 'Unknown'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-slate-500">
                      {entry.notes || '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  );
}
